<template>
  <div class="shadow rounded-lg">
    <YTable v-if="data" :data="data || []">
      <template slot="header-row">
        <th></th>
        <th>BID</th>
        <th>Destination Url</th>
        <th>Active</th>
        <th>Cap Frequency</th>
        <th>Cap Number</th>
        <th>Country Codes</th>
        <th>Domains</th>
        <th>Referers</th>
        <th>IPs</th>
        <th>Keywords</th>
        <th>subIds</th>
        <th>Reserved Urls</th>
        <th></th>
      </template>
      <template slot="row" slot-scope="props">
        <td>
          <div class="flex flex-col w-40">
            <p class="font-semibold text-blue-700">{{ props.rowData.label }}</p>
            <p class="text-xs text-gray-600">RTB-{{ props.rowData.id }}</p>
          </div>
        </td>
        <td class="font-semibold text-gray-900">
          ${{ props.rowData.bid.toFixed(2) }}
        </td>
        <td class="font-semibold text-gray-900">
          {{ props.rowData.destinationUrl }}
        </td>
        <td>
          <ToggleButton
            v-model="props.rowData.active"
            :sync="true"
            :color="toggleColors"
            :labels="{ checked: 'Active', unchecked: 'Paused' }"
            :width="90"
            :height="35"
            :font-size="12"
            style="margin: 0px;"
            @change="updateStatus($event, props.rowData)"
          />
        </td>
        <td>{{ props.rowData.capFrequency }}</td>
        <td>{{ props.rowData.capNumber }}</td>
        <td>
          <span
            v-if="props.rowData.filters?.countryCodes"
            class="block flex gap-1"
          >
            <Tag
              v-for="geo in props.rowData.filters.countryCodes"
              :key="geo"
              :color="geo.startsWith('!') ? 'red' : 'green'"
              >{{ geo }}</Tag
            >
          </span>
        </td>
        <td :class="getTextColor(props.rowData.filters?.domains)">
          {{ props.rowData.filters?.domains?.join(', ') }}
        </td>

        <td :class="getTextColor(props.rowData.filters?.referers)">
          {{ props.rowData.filters?.referers?.join(', ') }}
        </td>
        <td :class="getTextColor(props.rowData.filters?.ips)">
          {{ props.rowData.filters?.ips?.join(', ') }}
        </td>
        <td :class="getTextColor(props.rowData.filters?.keywords)">
          {{ props.rowData.filters?.keywords?.join(', ') }}
        </td>
        <td :class="getTextColor(props.rowData.filters?.subIds)">
          {{ props.rowData.filters?.subIds?.join(', ') }}
        </td>
        <td :class="getTextColor(props.rowData.filters?.reservedUrls)">
          {{ props.rowData.filters?.reservedUrls?.join(', ') }}
        </td>

        <td style="padding: 0">
          <v-popover
            offset="10"
            placement="left-start"
            :autoHide="true"
            popoverClass
            :class="['sidebar-user-popover']"
            popoverBaseClass="popover-menu sidebar-popover-menu"
            popoverInnerClass="popover-menu-inner animated animated-speed1 fadeIn"
          >
            <YButton
              class="btn btn-transparent btn-rounded tooltip-target text-gray-700 "
            >
              <Icon name="dotsVertical" :size="4" />
            </YButton>

            <!-- This will be the content of the popover -->
            <template slot="popover">
              <div class="list-group">
                <template>
                  <button
                    @click="openEditModal(props.rowData)"
                    class="text-blue-500 hover:bg-blue-100 list-group-item list-group-item-action"
                  >
                    <span class="flex items-center">
                      <Icon name="pencil" :size="4" class="mr-2" />Edit
                    </span>
                  </button>
                  <ClickAndReplace :duration="3000">
                    <button
                      class="text-red-500 hover:bg-red-100 list-group-item list-group-item-action"
                    >
                      <span class="flex items-center">
                        <Icon
                          name="trashCanOutline"
                          :size="4"
                          class="mr-2"
                        />Delete
                      </span>
                    </button>
                    <template v-slot:replacement>
                      <button
                        class="bg-red-500 text-white hover:bg-red-600 list-group-item list-group-item-action"
                        @click="deleteCampaignRtb(props.rowData)"
                      >
                        Confirm Delete
                      </button>
                    </template>
                  </ClickAndReplace>
                </template>
              </div>
            </template>
          </v-popover>
        </td>
      </template>
    </YTable>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { ToggleButton } from 'vue-js-toggle-button'
import UPSERT_CAMPAIGN_RTB from '@/graphql/Campaign/UpsertCampaignRtb.gql'
import RTBUpsertModal from '@/views/Admin/Campaign/RTBUpsertModal.vue'
import ClickAndReplace from '@/components/ui/ClickAndReplace'

export default {
  name: 'Campaign-RTB-Table',
  components: {
    ToggleButton,
    ClickAndReplace
  },
  props: {
    data: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      toggleColors: {
        checked: '#41b883',
        unchecked: '#888888',
        disabled: '#CCCCCC'
      }
    }
  },
  methods: {
    openEditModal(props) {
      this.$modal.show(
        RTBUpsertModal,
        { data: props },
        {
          height: 'auto',
          width: '80%',
          maxWidth: 600,
          adaptive: true,
          scrollable: true
        }
      )
    },
    deleteCampaignRtb(props) {
      this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteCampaignRtb($id: ID!) {
              deleteCampaignRtb(id: $id) {
                id
              }
            }
          `,
          variables: {
            id: props.id
          }
        })
        .then(() => {
          this.$events.fire('refetchCampaignRtbs')
          this.$toastr.s('RTB Deleted', 'Success!')
        })
        .catch(error => {
          this.$toastr.e(error, 'Error')
        })
    },
    updateStatus(event, props) {
      let newStatus = event.value
      this.$apollo
        .mutate({
          mutation: UPSERT_CAMPAIGN_RTB,
          variables: {
            input: {
              id: props.id,
              active: newStatus
            }
          }
        })
        .then(() => {
          this.isSaving = false
          this.$emit('close')
          this.$events.fire('refetchCampaignRtbs')
          this.$toastr.s('RTB Status Changed')
        })
        .catch(error => {
          this.$toastr.e(error, 'Error')
        })
    },
    getTextColor(arr) {
      return Array.isArray(arr) && arr[0]?.startsWith('!') ? 'text-red-500' : ''
    }
  }
}
</script>
